/* eslint-disable react/prop-types, react/jsx-one-expression-per-line, max-len, react/self-closing-comp, react/no-array-index-key, react/jsx-props-no-spreading */
import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import './styles/titulaya.css';
import chiploLogo from '../../assets/images/chiplo.svg';
import { AVISO_PRIVACIDAD_ROUTE } from '../../constants';

const WA_NUMBER = '523335063714';
const WA_TEXT = encodeURIComponent('Hola, me interesa TitulaYa para mi desarrollo.');
const WA_URL = `https://wa.me/${WA_NUMBER}?text=${WA_TEXT}`;
const SCHEDULE_IMAGE = 'https://fintecimal-common.s3.us-east-1.amazonaws.com/chiplo/SolucionTitulaYa.png';

const MONTSERRAT_HREF = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Open+Sans:wght@400;500;600;700&display=swap';

function useGoogleFonts() {
  useEffect(() => {
    if (document.querySelector(`link[href="${MONTSERRAT_HREF}"]`)) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = MONTSERRAT_HREF;
    link.dataset.titulaya = '1';
    document.head.appendChild(link);
  }, []);
}

// Constantes de variantes de fondo — úsalas siempre en lugar de strings literales
const BG = {
  WHITE: 'white',
  SUBTLE_PURPLE: 'subtle-purple',
  PURPLE_SOFT: 'purple-soft',
  PURPLE_DARK: 'purple-dark',
  PURPLE_HERO: 'purple-hero',
};

// Configuración interna de cada variante — no usar directamente fuera de PageSection
const SECTION_VARIANTS = {
  [BG.WHITE]: { cls: '', style: null },
  [BG.SUBTLE_PURPLE]: { cls: '', style: { background: 'linear-gradient(to bottom, #fbf9fc, #faf9fd)' } },
  [BG.PURPLE_SOFT]: { cls: 'bg-gradient-to-b from-brand-purple-50 to-white', style: null },
  [BG.PURPLE_DARK]: { cls: 'bg-brand-purple-700 text-white', style: null },
  [BG.PURPLE_HERO]: { cls: 'bg-gradient-to-br from-brand-purple-700 via-brand-purple-600 to-brand-purple-800 text-white', style: null },
};

// Lookup de anchos de contenedor (strings literales para que Tailwind los detecte en el build)
const CONTAINER_WIDTH = {
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
};

/**
 * Componente base para secciones de la página.
 *
 * Props:
 *   id              – id HTML para navegación por anclas
 *   bg              – variante de fondo: usa las constantes BG.WHITE | BG.SUBTLE_PURPLE | BG.PURPLE_SOFT | BG.PURPLE_DARK | BG.PURPLE_HERO
 *   container       – si true (default), envuelve children en un div max-w / mx-auto / px-6
 *   maxWidth        – ancho del contenedor: '4xl' | '5xl' | '6xl' (default '6xl')
 *   className       – clases extra sobre el <section>
 *   containerClassName – clases extra sobre el div contenedor interno
 *   style           – estilos inline extra sobre el <section>
 */
function PageSection({
  id,
  bg = BG.WHITE,
  container = true,
  maxWidth = '6xl',
  className = '',
  containerClassName = '',
  style,
  children,
}) {
  const variant = SECTION_VARIANTS[bg] ?? SECTION_VARIANTS[BG.WHITE];
  const sectionStyle = variant.style ? { ...variant.style, ...style } : style;
  const sectionClass = ['scroll-mt-16', variant.cls, className].filter(Boolean).join(' ');
  const containerClass = [CONTAINER_WIDTH[maxWidth] ?? CONTAINER_WIDTH['6xl'], 'mx-auto px-6', containerClassName].filter(Boolean).join(' ');

  return (
    <section id={id} className={sectionClass} style={sectionStyle || undefined}>
      {container ? <div className={containerClass}>{children}</div> : children}
    </section>
  );
}

export default function TitulaYaPage({ variant = 'titulaya' }) {
  useGoogleFonts();
  return (
    <div className="ty-scope min-h-screen">
      <Nav variant={variant} />
      <Hero />
      <Problem />
      <Solution />
      <Comparison />
      <Experiences />
      <Timeline />
      {/* <FAQ /> */}
      <CtaFinal />
      <Footer />
    </div>
  );
}

function WhatsappIcon({ className = 'w-5 h-5' }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488z" />
    </svg>
  );
}

const NAV_LINKS = [
  { href: '#problema', label: 'El Problema' },
  { href: '#solucion', label: 'La Solución' },
  { href: '#por-que-chiplo', label: '¿Por qué Chiplo?' },
];

function MenuToggleIcon({ open }) {
  return (
    <div className="relative w-6 h-6">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-6 h-6 absolute inset-0 transition-all duration-300"
        style={{
          opacity: open ? 0 : 1,
          transform: open ? 'rotate(90deg) scale(0.75)' : 'rotate(0deg) scale(1)',
        }}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-6 h-6 absolute inset-0 transition-all duration-300"
        style={{
          opacity: open ? 1 : 0,
          transform: open ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.75)',
        }}
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );
}

function MobileDrawer({ open, onClose }) {
  if (!open) return null;

  return (
    <div className="md:hidden">
      <div
        className="fixed inset-0 z-40"
        style={{ backgroundColor: 'rgba(15,23,42,0.5)', top: '4rem' }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed left-0 right-0 z-50 shadow-xl flex flex-col overflow-hidden"
        style={{
          top: '4rem',
          height: '50vh',
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <nav className="flex flex-col px-8 pt-8 pb-10 gap-8 text-lg font-medium text-slate-700 border-b border-slate-100">
          {NAV_LINKS.map(({ href, label }) => (
            <a
              key={href}
              href={href}
              className="hover:text-brand-purple-700 transition"
              style={{ color: '#334155' }}
              onClick={onClose}
            >
              {label}
            </a>
          ))}
        </nav>
        <div className="mt-auto px-8 pb-8">
          <a
            href={WA_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-2 rounded-full transition text-white font-semibold text-sm px-4 py-3 w-full"
            style={{ backgroundColor: '#f5a623' }}
          >
            <WhatsappIcon className="w-4 h-4" />
            Solicitar diagnóstico
          </a>
        </div>
      </div>
    </div>
  );
}

function Nav({ variant = 'titulaya' }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2">
          {variant === 'chiplo' ? (
            <img src={chiploLogo} alt="Chiplo" className="h-9 w-auto" />
          ) : (
            <span className="flex items-baseline gap-2">
              <span className="font-display font-bold text-2xl text-brand-purple-700 tracking-tight">TitulaYa</span>
              <span className="text-xs text-slate-500">by Chiplo</span>
            </span>
          )}
        </a>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
          {NAV_LINKS.map(({ href, label }) => (
            <a key={href} href={href} className="hover:text-brand-purple-700">{label}</a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={WA_URL}
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 transition text-white font-semibold text-sm px-4 py-2 whitespace-nowrap"
            aria-label="Solicitar diagnóstico por WhatsApp"
          >
            <WhatsappIcon className="w-4 h-4" />
            Solicitar diagnóstico
          </a>
          <button
            type="button"
            className="md:hidden p-2 rounded-md text-slate-600 hover:text-brand-purple-700 transition"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={mobileOpen}
          >
            <MenuToggleIcon open={mobileOpen} />
          </button>
        </div>
      </div>

      <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} />
    </header>
  );
}

function Hero() {
  return (
    <PageSection id="top" bg={BG.SUBTLE_PURPLE} container={false} className="relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" />
      <div className="relative max-w-4xl mx-auto px-6 pt-6 pb-14 text-center">
        <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-purple-700 bg-brand-purple-100 rounded-full px-3 py-1">
          Para desarrolladores inmobiliarios
        </p>
        <h1 className="mt-8 font-display font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-slate-900">
          El capital se recupera al escriturar<br />
          <span className="text-brand-purple-700 text-4xl md:text-5xl lg:text-6xl">No al vender</span>
        </h1>
        <p className="mt-8 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
          <span className="text-slate-900 font-semibold">Integramos una capa especializada de gestión y seguimiento para acelerar la escrituración de todos tus clientes y ayudarte a recuperar capital más rápido.</span>{' '}
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <a
            href={WA_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 transition text-white font-semibold px-7 py-3.5 text-lg shadow-lg shadow-brand-orange-500/30"
          >
            <WhatsappIcon className="w-5 h-5" />
            Solicitar diagnóstico
          </a>
          <a
            href="#solucion"
            className="inline-flex items-center gap-2 rounded-full border border-brand-purple-200 hover:border-brand-purple-500 text-brand-purple-700 font-semibold px-7 py-3.5 text-lg transition"
          >
            La Solución →
          </a>
        </div>
      </div>
    </PageSection>
  );
}

function Problem() {
  const items = [
    {
      n: '01',
      title: 'Capital inmovilizado',
      body: 'Cada escritura pendiente retrasa la recuperación del capital necesario para iniciar nuevas etapas o proyectos. Sin escrituración, el dinero no regresa al negocio.',
    },
    {
      n: '02',
      title: 'Crédito puente acumulando intereses',
      body: 'Cada retraso en la escrituración prolonga el costo financiero del proyecto y reduce la velocidad de recuperación de la inversión.',
    },
    {
      n: '03',
      title: 'El reto no es sólo coordinar. Es resolver.',
      body: 'La escrituración involucra bancos, brókers, clientes, notarías y áreas internas del desarrollo. Cuando surgen situaciones que requieren seguimiento especializado, los expedientes se retrasan y la recuperación de capital se vuelve más lenta.',
    },
  ];
  return (
    <PageSection id="problema" className="py-24">
      <div className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-orange-600">El problema</p>
        <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-slate-900 leading-tight">
          Tu obra está lista. Tu capital sigue detenido.
        </h2>
        <p className="mt-4 text-lg text-slate-600">
          Cada unidad vendida que no se ha escriturado retrasa la recuperación de capital y limita tu capacidad para seguir creciendo.
        </p>
      </div>
      <div className="mt-12 grid md:grid-cols-3 gap-6">
        {items.map((it) => (
          <div key={it.n} className="bg-white rounded-card shadow-card border border-slate-100 p-6">
            <div className="font-display font-bold text-4xl text-brand-purple-200">{it.n}</div>
            <h3 className="mt-3 font-display font-semibold text-xl text-slate-900">{it.title}</h3>
            <p className="mt-3 text-slate-600 leading-relaxed">{it.body}</p>
          </div>
        ))}
      </div>
    </PageSection>
  );
}

function Solution() {
  const steps = [
    {
      n: 'A',
      title: 'Nos integramos a tu operación',
      body: 'Trabajamos junto a tu equipo de titulación como una extensión especializada del área, ampliando capacidad y seguimiento sin incrementar tu estructura interna.',
    },
    {
      n: 'B',
      title: 'Nos anticipamos a los retrasos',
      body: 'Contactamos a cada cliente desde etapas tempranas para confirmar su método de pago, identificar pendientes y evitar que los problemas aparezcan cuando la obra está lista para escriturar.',
    },
    {
      n: 'C',
      title: 'Resolvemos los casos que requieren especialización',
      body: 'La escrituración involucra crédito, documentación, seguimiento al cliente, coordinación con notaría y gestión operativa. Nuestro equipo interviene donde normalmente se generan los retrasos que ponen en riesgo el cierre.',
    },
  ];
  return (
    <PageSection id="solucion" bg={BG.SUBTLE_PURPLE} container={false} className="py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-orange-600">La solución</p>
          <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-slate-900 leading-tight">
            Un solo equipo, Un solo objetivo.
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            <span className="font-semibold text-brand-purple-700">TitulaYa </span>es la solución especializada de Chiplo para desarrolladores inmobiliarios. Nos integramos a tu operación para coordinar todos los participantes del proceso y mantener cada cliente avanzando hacia la firma en el menor tiempo posible.
          </p>
        </div>
        <ol className="mt-14 grid gap-6 relative" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {steps.map((s) => (
            <li key={s.n} className="bg-white rounded-card shadow-card border border-slate-100 p-6 relative">
              <div className="w-10 h-10 rounded-full bg-brand-purple-700 text-white flex items-center justify-center font-display font-bold">
                {s.n}
              </div>
              <h3 className="mt-3 font-display font-semibold text-xl text-slate-900">{s.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
      <Schedule />
    </PageSection>
  );
}

function Schedule() {
  return (
    <section id="schedule" className="pt-0 scroll-mt-16">
      <hr className="max-w-6xl mx-6 xl:mx-auto mt-12" style={{ borderColor: 'rgba(191, 181, 236, 0.5)', borderTopWidth: '0.5px' }} />
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-slate-900 leading-tight">
            Orquestamos todo el proceso de escrituración
          </h2>
          <p className="mb-6 text-lg text-slate-600">
            Mientras cada participante atiende una parte del proceso, <span className="font-semibold text-brand-purple-700">TitulaYa </span> ayuda a coordinar el conjunto completo para mantener cada cliente avanzando hacia la firma lo más rápido posible.
          </p>
        </div>
      </div>
      <div className="mx-0 md:max-w-6xl mx-auto">
        <img src={SCHEDULE_IMAGE} alt="Orquestación" className="w-full h-auto select-none pointer-events-none" draggable={false} />
      </div>
    </section>
  );
}

function Comparison() {
  const rows = [
    { label: 'Anticipar riesgos', broker: 'Los riesgos suelen detectarse cuando la fecha de firma ya está cerca.', titulaya: 'Los riesgos se identifican con anticipación para tomar acciones correctivas.' },
    { label: 'Más capacidad, misma estructura', broker: 'La capacidad depende exclusivamente del tiempo disponible de tu equipo interno.', titulaya: 'Una capa especializada aumenta la capacidad operativa sin incrementar estructura interna.' },
    { label: 'Seguimiento especializado y dedicado', broker: 'El seguimiento al cliente compite contra prioridades jurídicas, financieras y administrativas.', titulaya: 'Equipo dedicado exclusivamente a mantener cada cliente avanzando hacia la firma.' },
    { label: 'Coordinación de todos los participantes', broker: 'La coordinación con clientes, brókers, bancos y notarías se realiza de forma independiente.', titulaya: 'Todos los participantes son gestionados sobre una misma ruta de trabajo hasta la escrituración.' },
  ];
  return (
    <PageSection id="por-que-chiplo" className="py-24">
      <div className="max-w-4xl">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-orange-600">¿Por qué Chiplo?</p>
        <h2 className="mt-3 mb-0 font-display font-bold text-3xl md:text-4xl text-slate-900 leading-tight">
          No sustituimos tu equipo de titulación.
        </h2>
        <h2 className="my-0 font-display font-bold text-3xl md:text-4xl text-slate-900 leading-tight">
          Lo potenciamos con una capa especializada
        </h2>
      </div>

      <div className="mt-10 hidden md:block overflow-hidden rounded-card border border-slate-200 bg-white shadow-card">
        <div className="grid grid-cols-3 bg-slate-50 text-sm font-semibold text-slate-600">
          <div className="p-5"></div>
          <div className="p-5 border-l border-slate-200">Sin TitulaYa</div>
          <div className="p-5 border-l border-brand-purple-100 bg-brand-purple-700 text-white">Con TitulaYa</div>
        </div>
        {rows.map((r, i) => (
          <div key={r.label} className={`grid grid-cols-3 text-sm ${i % 2 ? 'bg-slate-50/40' : 'bg-white'}`}>
            <div className="p-5 font-semibold text-slate-900">{r.label}</div>
            <div className="p-5 border-l border-slate-200 text-slate-600">{r.broker}</div>
            <div className="p-5 border-l border-slate-200 text-slate-800 font-medium">
              <span className="inline-flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-brand-green-500/15 text-brand-green-500 flex items-center justify-center text-xs font-bold">✓</span>
                {r.titulaya}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 md:hidden space-y-4">
        {rows.map((r) => (
          <div key={r.label} className="rounded-card border border-slate-200 bg-white shadow-card overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 font-display font-semibold text-slate-900 border-b border-slate-200">
              {r.label}
            </div>
            <div className="px-5 py-4 border-b border-slate-100">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Sin TitulaYa</div>
              <div className="text-sm text-slate-600">{r.broker}</div>
            </div>
            <div className="px-5 py-4 bg-brand-purple-50">
              <div className="text-xs font-semibold uppercase tracking-wider text-brand-purple-700 mb-1">Con TitulaYa</div>
              <div className="text-sm text-slate-800 font-medium flex items-start gap-2">
                <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-brand-green-500/15 text-brand-green-500 flex items-center justify-center text-xs font-bold">✓</span>
                <span>{r.titulaya}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="max-w-3xl mx-auto" style={{ textAlign: 'center' }}>
        <h2 className="my-10 text-brand-purple-700 text-2xl md:text-3xl lg:text-4xl">
          El objetivo es tener más capacidad para escriturar y recuperar capital más rápido
        </h2>
      </div>
    </PageSection>
  );
}

function IconPeople() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function IconDocumentCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M16 13l2 2 4-4" />
      <line x1="8" y1="13" x2="12" y2="13" />
      <line x1="8" y1="9" x2="12" y2="9" />
    </svg>
  );
}

function IconMoney() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  );
}

function IconChart() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
      <polyline points="3 20 21 20" />
      <polyline points="6 14 12 8 16 12 20 6" />
      <polyline points="18 6 20 6 20 8" />
    </svg>
  );
}

function IconStar() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function Experiences() {
  const stats = [
    {
      icon: <IconPeople />,
      value: '+850',
      label: 'clientes perfilados para crédito hipotecario.',
      img: 'https://fintecimal-common.s3.us-east-1.amazonaws.com/chiplo/titulaya-experiencia-01.png',
    },
    {
      icon: <IconDocumentCheck />,
      value: '+210',
      label: 'créditos formalizados.',
      img: 'https://fintecimal-common.s3.us-east-1.amazonaws.com/chiplo/titulaya-experiencia-02.png',
    },
    {
      icon: <IconMoney />,
      value: '$675+',
      label: 'millones gestionados en financiamiento hipotecario.',
      img: 'https://fintecimal-common.s3.us-east-1.amazonaws.com/chiplo/titulaya-experiencia-03.png',
    },
    {
      icon: <IconChart />,
      value: '24%',
      label: 'de conversión de cliente a escrituración en canal propio.',
      img: 'https://fintecimal-common.s3.us-east-1.amazonaws.com/chiplo/titulaya-experiencia-04.png',
    },
  ];

  return (
    <PageSection id="experiences" bg={BG.SUBTLE_PURPLE} className="py-16">
      <div className="overflow-hidden">
        <div className="px-8 md:px-14 pt-12 pb-10 text-center">
          <div className="flex items-center justify-center gap-3">
            <span className="w-6 md:w-12 h-0.5 bg-brand-orange-400 opacity-60 shrink-0" />
            <p className="text-2xl md:text-5xl font-semibold uppercase tracking-widest text-brand-orange-400 whitespace-nowrap">Experiencia detrás de</p>
            <span className="w-6 md:w-12 h-0.5 bg-brand-orange-400 opacity-60 shrink-0" />
          </div>
          <h2 className="mt-3 mb-0 font-display font-bold text-2xl md:text-5xl text-white leading-tight">
            <span className="text-brand-orange-400">TitulaYa</span>
          </h2>
          <div className="mt-6 inline-flex items-center gap-3 rounded-full px-0 py-3">
            <div className="w-9 h-9 rounded-full border border-brand-orange-400 flex items-center justify-center text-brand-orange-400 shrink-0">
              <IconStar />
            </div>
            <p className="text-lg md:text-xl text-left font-display font-bold text-slate-900">
              <span className="font-bold text-brand-orange-400">12 años</span> gestionando productos financieros<br className="hidden md:block" /> con servicio personalizado y tecnología.
            </p>
          </div>
        </div>

        <div className="border-t border-yellow/10 px-8 md:px-14 pb-10 pt-0">
          <p className="font-display font-bold text-center text-lg md:text-xl uppercase tracking-widest text-slate-700 mb-6">En los últimos 2 años:</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {stats.map((s) => (
              <div key={s.value} className="bg-white rounded-card shadow-card border border-slate-100 px-8 py-14 flex flex-col items-center text-center">
                <img src={s.img} alt={s.label} className="w-20 h-20 object-contain mb-4" />
                <div className="font-display font-bold text-4xl text-brand-purple-600">{s.value}</div>
                <p className="mt-3 text-base leading-snug text-slate-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </PageSection>
  );
}

function Timeline() {
  const steps = [
    {
      when: 'Día −90 · antes del avalúo',
      title: 'Identificamos riesgos',
      body: 'Confirmamos forma de pago, detectamos posibles bloqueos y damos visibilidad temprana sobre cada cliente.',
      isAnchor: false,
    },
    {
      when: 'Día −30 · antes del avalúo',
      title: 'Aseguramos viabilidad',
      body: 'Gestionamos créditos, alineamos participantes y resolvemos riesgos antes de llegar al avalúo.',
      isAnchor: false,
    },
    {
      when: 'Avalúo · Día 0',
      title: 'Cliente listo para escriturar',
      body: 'Cuando la propiedad está lista, el cliente también.',
      isAnchor: true,
    },
    {
      when: 'Escrituración',
      title: 'Firma y recuperación de capital',
      body: 'Coordinamos los últimos pasos para llegar a escritura lo más rápido posible.',
      isAnchor: false,
    },
  ];
  return (
    <PageSection bg={BG.PURPLE_DARK} container={false} className="py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-semibold uppercase tracking-wider text-brand-orange-400">Línea de tiempo</p>
          <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl leading-tight">
            Escrituración que empieza meses antes de la firma
          </h2>
        </div>

        <div className="mt-14 grid md:grid-cols-4 gap-x-6 gap-y-0 relative">
          <div className="hidden md:block absolute top-5 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-brand-orange-500/0 via-brand-orange-500/60 to-brand-orange-500/0" />
          {steps.map((s, i) => (
            <div key={s.when} className="relative">
              {s.isAnchor ? (
                <div className="relative z-10 w-10 h-10 flex items-center justify-center">
                  <div className="w-7 h-7 bg-brand-orange-500 rotate-45 ring-4 ring-brand-orange-500/20 flex items-center justify-center">
                    <span className="-rotate-45 font-display font-bold text-sm text-white">{i + 1}</span>
                  </div>
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-brand-orange-500 text-white flex items-center justify-center font-display font-bold text-sm relative z-10">
                  {i + 1}
                </div>
              )}
              <div className="mt-4 pb-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-orange-400">{s.when}</div>
                <h3 className="mt-1 font-display font-semibold text-lg text-white leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-brand-purple-100 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageSection>
  );
}

// eslint-disable-next-line no-unused-vars
function FAQ() {
  const items = [
    {
      q: '¿Quién paga por el servicio de TitulaYa?',
      a: 'El servicio no tiene costo para el desarrollador. Chiplo se remunera por el lado del banco con cada crédito cerrado. Lo único que necesitamos es acceso al pipeline de compradores en la etapa correspondiente.',
    },
    {
      q: '¿Qué pasa si mi comprador paga de contado?',
      a: 'Todos tus clientes reciben servicio, no solo los de crédito. Integramos el expediente del cliente, confirmamos método de pago y lo canalizamos con tu área de titulación para seguir con el proceso.',
    },
    {
      q: '¿Cómo se integra TitulaYa con nuestro CRM / ERP?',
      a: 'Nos conectamos con tu sistema interno para recibir la lista de compradores cuando la obra entra a fase de entrega. Soportamos Excel, API o integración directa según el caso.',
    },
    {
      q: '¿Cuánto tarda en arrancar el servicio?',
      a: 'Podemos arrancar la integración en una semana. Recomendamos iniciar con un desarrollo piloto y escalar al resto del portafolio.',
    },
  ];
  return (
    <PageSection id="faq" bg={BG.PURPLE_SOFT} maxWidth="4xl" className="py-24">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-sm font-semibold uppercase tracking-wider text-brand-orange-600">Preguntas frecuentes</p>
        <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl text-slate-900 leading-tight">
          Lo que todo director de titulación nos pregunta.
        </h2>
      </div>
      <div className="mt-12 divide-y divide-slate-200 border border-slate-200 rounded-card bg-white shadow-card">
        {items.map((it, i) => <FAQItem key={i} {...it} defaultOpen={i === 0} />)}
      </div>
    </PageSection>
  );
}

function FAQItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 px-6 py-5 text-left"
        aria-expanded={open}
      >
        <span className="font-display font-semibold text-slate-900">{q}</span>
        <span className={`text-brand-purple-700 text-2xl leading-none transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
      </button>
      {open && (
        <div className="px-6 pb-6 text-slate-600 leading-relaxed">{a}</div>
      )}
    </div>
  );
}

function CtaFinal() {
  return (
    <PageSection bg={BG.PURPLE_HERO} maxWidth="4xl" className="relative overflow-hidden" containerClassName="py-20 text-center">
      <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight">
        El capital se recupera al escriturar.
      </h2>
      <p className="mt-4 text-brand-purple-100 text-lg max-w-2xl mx-auto">
        Analicemos tu proceso actual e identifiquemos oportunidades para acelerar la escrituración y recuperar capital más rápido.
      </p>
      <div className="mt-10 flex justify-center">
        <a
          href={WA_URL}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 rounded-full bg-brand-orange-500 hover:bg-brand-orange-600 transition text-white font-semibold px-8 py-4 text-lg shadow-xl shadow-black/20"
        >
          <WhatsappIcon className="w-6 h-6" />
          Solicitar diagnóstico
        </a>
      </div>
    </PageSection>
  );
}

function Footer() {
  return (
    <footer className="bg-slate-900 text-slate-400">
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" style={{ paddingTop: '1.5rem', paddingBottom: '0.5rem' }}>
        <div>
          <div className="flex items-baseline gap-2">
            <span className="font-display font-bold text-xl text-white">TitulaYa</span>
            <span className="text-xs text-slate-500">by Chiplo</span>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-5 text-xs text-slate-500 flex items-center justify-between">
          <span>© {new Date().getFullYear()} Chiplo. Todos los derechos reservados.</span>
          <Link to={AVISO_PRIVACIDAD_ROUTE} target="_blank" rel="noreferrer" className="hover:text-white">Aviso de privacidad</Link>
        </div>
      </div>
    </footer>
  );
}
